import type { HTMLAttributes, ReactNode } from 'react';
import type { SacInfo, SacKey } from '@/content/sacInfo';
import { InfoTooltip } from '@/components/InfoTooltip';

/**
 * CWY Card — the universal hairline-bordered paper surface.
 * Optional title (forest) + hint. Everything in CWY lives in one of these.
 * Optional `info` adds an accessible ⓘ tooltip beside the title (additive —
 * cards without it render exactly as before).
 */
export interface CardProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title?: ReactNode;
  hint?: ReactNode;
  padding?: string;
  info?: SacKey | SacInfo;
}

export function Card({ title, hint, info, children, padding = '16px 18px', style, ...rest }: CardProps) {
  return (
    <section
      style={{
        background: 'var(--surface-card)',
        border: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-lg)',
        padding,
        ...style,
      }}
      {...rest}
    >
      {(title || hint || info) && (
        <header style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '12px' }}>
          {title && (
            <h3 style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-h3)', fontWeight: 600, color: 'var(--text-accent)' }}>
              {title}
            </h3>
          )}
          {info && (
            <span style={{ alignSelf: 'center', display: 'inline-flex' }}>
              <InfoTooltip info={info} />
            </span>
          )}
          {hint && (
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--text-hint)' }}>
              {hint}
            </span>
          )}
        </header>
      )}
      {children}
    </section>
  );
}
